import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch,
  MdViewModule,
  MdViewList,
  MdChevronLeft,
  MdChevronRight,
  MdMailOutline,
  MdPhone,
} from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { getInitials } from '../../utils/attendanceFormatters';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import {
  readSavedFilter,
  writeSavedListFilters,
} from '../../utils/admissionsListFilters';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const PAGE_SIZE = 80;
const FILTER_KEY = 'students';

const STATE_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Registration' },
  { value: 'fee_paid', label: 'Admission Fee Paid' },
  { value: 'admitted', label: 'Admitted' },
  { value: 'detain', label: 'Detain' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'undergraduate', label: 'Undergraduate' },
  { value: 'postgraduate', label: 'Postgraduate' },
];

const ACTIVE_OPTIONS = [
  { value: '', label: 'Active + archived' },
  { value: 'true', label: 'Active only' },
  { value: 'false', label: 'Archived only' },
];

const CAMPUS_FALLBACK = [
  { value: 'prof_addow', label: 'Prof Addow Campus' },
  { value: 'prof_shahiid', label: 'Prof Shahiid Campus' },
  { value: 'prof_shurie', label: 'Prof Shurie Campus' },
];

function stateClass(state) {
  const key = String(state || '').toLowerCase();
  if (key === 'admitted') return 'ea-adm-state--admitted';
  if (key === 'fee_paid') return 'ea-adm-state--fee';
  if (key === 'detain') return 'ea-adm-state--detain';
  if (key === 'cancelled') return 'ea-adm-state--cancelled';
  return 'ea-adm-state--draft';
}

function resolveImageUrl(row, baseURL) {
  const raw = row.image_url || row.image;
  if (!raw || raw === 'false') return null;
  const text = String(raw);
  if (text.startsWith('data:') || text.startsWith('http')) return text;
  // Bare base64 from API (no data: prefix)
  if (text.length > 64 && !text.includes('/') && !text.includes(' ')) {
    return `data:image/jpeg;base64,${text}`;
  }
  // /web/image requires Odoo session — portal Bearer auth cannot use it
  if (text.includes('/web/image/')) return null;
  const base = (baseURL || '').replace(/\/$/, '');
  return `${base}${text.startsWith('/') ? text : `/${text}`}`;
}

function StudentAvatar({ row }) {
  const [broken, setBroken] = useState(false);
  const src = resolveImageUrl(row, admissionsAPI.baseURL);
  if (src && !broken) {
    return (
      <img
        className="ea-odoo-avatar__img"
        src={src}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className="ea-odoo-avatar__initials">{getInitials(row.name)}</span>;
}

export default function AdmissionsStudentsPage() {
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user } = useEmployeeAttendanceAuth();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(() => readSavedFilter(FILTER_KEY, 'offset', 0));
  const [viewMode, setViewMode] = useState(() =>
    readSavedFilter(FILTER_KEY, 'viewMode', 'kanban')
  );
  const [searchInput, setSearchInput] = useState(() =>
    readSavedFilter(FILTER_KEY, 'searchInput', '')
  );
  const [search, setSearch] = useState(() => readSavedFilter(FILTER_KEY, 'search', ''));
  const [campus, setCampus] = useState(() => readSavedFilter(FILTER_KEY, 'campus', ''));
  const [state, setState] = useState(() => readSavedFilter(FILTER_KEY, 'state', ''));
  const [facultyId, setFacultyId] = useState(() => readSavedFilter(FILTER_KEY, 'facultyId', ''));
  const [departmentId, setDepartmentId] = useState(() =>
    readSavedFilter(FILTER_KEY, 'departmentId', '')
  );
  const [batchId, setBatchId] = useState(() => readSavedFilter(FILTER_KEY, 'batchId', ''));
  const [intakeId, setIntakeId] = useState(() => readSavedFilter(FILTER_KEY, 'intakeId', ''));
  const [academicYearId, setAcademicYearId] = useState(() =>
    readSavedFilter(FILTER_KEY, 'academicYearId', '')
  );
  const [category, setCategory] = useState(() => readSavedFilter(FILTER_KEY, 'category', 'all'));
  const [activeFilter, setActiveFilter] = useState(() =>
    readSavedFilter(FILTER_KEY, 'activeFilter', '')
  );
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchInitRef = useRef(true);

  const openStudent = (id) => {
    navigate(`/bu-dashboard/admissions/students/${id}`);
  };

  const openNew = () => {
    navigate('/bu-dashboard/admissions/students/new');
  };

  const loadLookups = useCallback(async () => {
    const campusRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getCampuses(token)
    );
    if (!campusRes.error) {
      const list = campusRes.data?.campuses || campusRes.data?.records || [];
      if (list.length) setCampuses(list);
    }
    const facRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getFaculties(token)
    );
    if (!facRes.error) {
      setFaculties(facRes.data?.faculties || facRes.data?.records || []);
    }
    const intakeRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getIntakes(token)
    );
    if (!intakeRes.error) {
      setIntakes(intakeRes.data?.intakes || intakeRes.data?.records || []);
    }
    const yearRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getAcademicYears(token)
    );
    if (!yearRes.error) {
      setAcademicYears(yearRes.data?.academic_years || yearRes.data?.records || []);
    }
  }, [ensureHrToken]);

  const loadDepartments = useCallback(
    async (facId) => {
      if (!facId) {
        setDepartments([]);
        return;
      }
      const { data, error: err } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getDepartments(facId, token)
      );
      if (err) {
        setDepartments([]);
        return;
      }
      setDepartments(data?.departments || data?.records || []);
    },
    [ensureHrToken]
  );

  const loadBatches = useCallback(
    async (depId, facId) => {
      if (!depId && !facId) {
        setBatches([]);
        return;
      }
      const { data, error: err } = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getBatches(depId || undefined, token, {
          faculty_id: facId || undefined,
        })
      );
      if (err) {
        setBatches([]);
        return;
      }
      setBatches(data?.batches || data?.records || []);
    },
    [ensureHrToken]
  );

  const loadStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      search: search || undefined,
      campus: campus || undefined,
      state: state || undefined,
      faculty_id: facultyId ? Number(facultyId) : undefined,
      department_id: departmentId ? Number(departmentId) : undefined,
      batch_id: batchId ? Number(batchId) : undefined,
      admission_intake_id: intakeId ? Number(intakeId) : undefined,
      academic_year_id: academicYearId ? Number(academicYearId) : undefined,
      // Match Odoo Student Admissions: all categories + active/archived by default
      category: category || 'all',
      undergraduate_only: category === 'undergraduate',
      limit: PAGE_SIZE,
      offset,
    };
    if (activeFilter === 'true') params.active = true;
    if (activeFilter === 'false') params.active = false;

    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getStudents(params, token)
    );
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      setRecords([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setRecords([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setRecords(data.records || data.students || []);
    setTotal(Number(data.total) || 0);
    setLoading(false);
  }, [
    ensureHrToken,
    search,
    campus,
    state,
    facultyId,
    departmentId,
    batchId,
    intakeId,
    academicYearId,
    category,
    activeFilter,
    offset,
    user?.hr_api_error,
  ]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    writeSavedListFilters(FILTER_KEY, {
      offset,
      viewMode,
      searchInput,
      search,
      campus,
      state,
      facultyId,
      departmentId,
      batchId,
      intakeId,
      academicYearId,
      category,
      activeFilter,
    });
  }, [
    offset,
    viewMode,
    searchInput,
    search,
    campus,
    state,
    facultyId,
    departmentId,
    batchId,
    intakeId,
    academicYearId,
    category,
    activeFilter,
  ]);

  useEffect(() => {
    loadDepartments(facultyId);
  }, [facultyId, loadDepartments]);

  useEffect(() => {
    loadBatches(departmentId, facultyId);
  }, [departmentId, facultyId, loadBatches]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    if (searchInitRef.current) {
      searchInitRef.current = false;
      return undefined;
    }
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="ea-hr-dash ea-odoo-emp ea-adm">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <button type="button" className="ea-odoo-new" onClick={openNew}>
            New
          </button>
          <div className="ea-odoo-control__title">Students Admission</div>
        </div>

        <div className="ea-odoo-search">
          <MdSearch className="ea-odoo-search__icon" aria-hidden />
          <input
            type="search"
            placeholder="Search name, BU ID, Student ID, phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search students by name, BU ID, Student ID, or phone"
          />
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={viewMode === 'kanban' ? 'active' : ''}
              onClick={() => setViewMode('kanban')}
              title="Kanban"
            >
              <MdViewModule size={18} />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List"
            >
              <MdViewList size={18} />
            </button>
          </div>
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}</span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="ea-odoo-filters">
        <label>
          <span className="ea-field-label">Academic Year</span>
          <select
            className="ea-odoo-filters__select"
            value={academicYearId}
            onChange={(e) => {
              setOffset(0);
              setAcademicYearId(e.target.value);
            }}
          >
            <option value="">All academic years</option>
            {academicYears.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Admission Intake</span>
          <select
            className="ea-odoo-filters__select"
            value={intakeId}
            onChange={(e) => {
              setOffset(0);
              setIntakeId(e.target.value);
            }}
          >
            <option value="">All intakes</option>
            {intakes.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Faculty</span>
          <select
            className="ea-odoo-filters__select"
            value={facultyId}
            onChange={(e) => {
              setOffset(0);
              setFacultyId(e.target.value);
              setDepartmentId('');
              setBatchId('');
            }}
          >
            <option value="">All faculties</option>
            {faculties.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Department</span>
          <select
            className="ea-odoo-filters__select"
            value={departmentId}
            disabled={!facultyId}
            onChange={(e) => {
              setOffset(0);
              setDepartmentId(e.target.value);
              setBatchId('');
            }}
          >
            <option value="">{facultyId ? 'All departments' : 'Select faculty first'}</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Batch</span>
          <select
            className="ea-odoo-filters__select"
            value={batchId}
            disabled={!facultyId}
            onChange={(e) => {
              setOffset(0);
              setBatchId(e.target.value);
            }}
          >
            <option value="">{facultyId ? 'All batches' : 'Select faculty first'}</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Campus</span>
          <select
            className="ea-odoo-filters__select"
            value={campus}
            onChange={(e) => {
              setOffset(0);
              setCampus(e.target.value);
            }}
          >
            <option value="">All campuses</option>
            {campuses.map((c) => (
              <option key={c.value || c.id} value={c.value || c.id}>
                {c.label || c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Status</span>
          <select
            className="ea-odoo-filters__select"
            value={state}
            onChange={(e) => {
              setOffset(0);
              setState(e.target.value);
            }}
          >
            {STATE_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Category</span>
          <select
            className="ea-odoo-filters__select"
            value={category}
            onChange={(e) => {
              setOffset(0);
              setCategory(e.target.value);
            }}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Active</span>
          <select
            className="ea-odoo-filters__select"
            value={activeFilter}
            onChange={(e) => {
              setOffset(0);
              setActiveFilter(e.target.value);
            }}
          >
            {ACTIVE_OPTIONS.map((o) => (
              <option key={o.value || 'both'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      {viewMode === 'kanban' ? (
        <div className="ea-odoo-kanban">
          {loading && records.length === 0 ? (
            <p className="ea-hr-table__empty">Loading students…</p>
          ) : records.length === 0 ? (
            <p className="ea-hr-table__empty">No students found.</p>
          ) : (
            records.map((row) => (
              <article key={row.id} className="ea-odoo-card ea-adm-card">
                <button
                  type="button"
                  className="ea-odoo-card__main"
                  onClick={() => openStudent(row.id)}
                  aria-label={`Open ${row.name}`}
                >
                  <div className="ea-odoo-avatar">
                    <StudentAvatar row={row} />
                  </div>
                  <div className="ea-odoo-card__body">
                    <h3 className="ea-odoo-card__name">{row.name}</h3>
                    <p className="ea-odoo-card__line">
                      <span>{row.admission_number || '—'}</span>
                      {row.student_no ? <span> · ID {row.student_no}</span> : null}
                    </p>
                    {(row.phone || row.mobile) && (
                      <p className="ea-odoo-card__line">
                        <MdPhone aria-hidden />
                        <span>{row.phone || row.mobile}</span>
                      </p>
                    )}
                    {row.email && (
                      <p className="ea-odoo-card__line">
                        <MdMailOutline aria-hidden />
                        <span>{row.email}</span>
                      </p>
                    )}
                    <p className="ea-odoo-card__line">
                      <span>{row.campus_label || row.campus || '—'}</span>
                    </p>
                    <p className="ea-odoo-card__line">
                      <span>
                        {row.faculty_name || '—'}
                        {row.department_name ? ` · ${row.department_name}` : ''}
                      </span>
                    </p>
                  </div>
                </button>
                <div className="ea-odoo-card__footer">
                  <span className={`ea-adm-state ${stateClass(row.state)}`}>
                    {row.state_label || row.state || '—'}
                  </span>
                </div>
              </article>
            ))
          )}
        </div>
      ) : (
        <div className="ea-odoo-list-wrap">
          <table className="ea-odoo-list">
            <thead>
              <tr>
                <th>Name</th>
                <th>BU ID</th>
                <th>Student ID</th>
                <th>Campus</th>
                <th>Faculty</th>
                <th>Department</th>
                <th>Status</th>
                <th>Phone</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {loading && records.length === 0 ? (
                <tr>
                  <td colSpan={9} className="ea-hr-table__empty">
                    Loading students…
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={9} className="ea-hr-table__empty">
                    No students found.
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr
                    key={row.id}
                    className="ea-odoo-att-list__row"
                    onClick={() => openStudent(row.id)}
                  >
                    <td className="ea-odoo-list__name">{row.name}</td>
                    <td>{row.admission_number || ''}</td>
                    <td>{row.student_no || ''}</td>
                    <td>{row.campus_label || row.campus || ''}</td>
                    <td>{row.faculty_name || ''}</td>
                    <td>{row.department_name || ''}</td>
                    <td>
                      <span className={`ea-adm-state ${stateClass(row.state)}`}>
                        {row.state_label || row.state || ''}
                      </span>
                    </td>
                    <td>{row.phone || row.mobile || ''}</td>
                    <td>{row.email || ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
